/**
 * Daily Drop (04 §11, 08 §1): one seeded mission per UTC date, the same for everyone, flown by a
 * standard-issue Ranger of the Day - once. The screen shows today's mission card, says plainly
 * that there is one attempt, and keeps the local table of the days already flown, newest first.
 * FLY marks the day as played the moment the aircraft leaves - quitting is not a second attempt -
 * so it asks before it calls `deployDaily`.
 */

// The mission card is the briefing's, row for row: its classes come with it.
import '../briefing/briefing.css';
import '../tables.css';
import '../widgets/dossier.css';
import { mmss } from '../../hud/dom';
import type { Deployment } from '../../../meta/api';
import type { DailyEntry, DailyStatus } from '../../../meta/types';
import type { Outcome } from '../../../sim/types';
import { archetypeNumber, clampThreat } from '../archetypes';
import type { Ctx, Screen } from '../context';
import { box, button, el, errorMessage, panel, show } from '../dom';
import { frame } from '../widgets/frame';
import { segmentGauge } from '../widgets/gauge';
import { stamp } from '../widgets/stamp';
import { confirmDailyFlight, ONE_ATTEMPT_LINES } from './oneAttempt';
import { utcDate } from './utcDate';

/** The table, newest first; today's line leads when two entries share a date. Pure. */
export function tableOrder(entries: readonly DailyEntry[]): DailyEntry[] {
  return [...entries].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/**
 * How the day ended, in a word. The table does not know whether the objective was done - the
 * score says that - so an extraction is just that. `scrubbed` is a drop that was never finished.
 */
export const OUTCOME_WORD: Readonly<Record<Outcome, string>> = {
  extracted: 'EXTRACTED',
  kia: 'KIA',
  captured: 'CAPTURED',
  walkedOut: 'WALKED OUT',
  scrubbed: 'NOT FINISHED',
};

const cameHome = (entry: DailyEntry): boolean => entry.outcome === 'extracted' || entry.outcome === 'walkedOut';

function fact(label: string, value: string): HTMLElement {
  return box('menu-brief-row', el('span', 'menu-dim', label), el('span', 'menu-paper', value));
}

function missionCard(ctx: Ctx, status: DailyStatus): HTMLElement {
  const p = status.params;
  const card = panel("TODAY'S MISSION", 'menu-daily-card menu-grow');
  const archetype = ctx.describe(p.archetype);
  const name = el('div', 'menu-brief-name', `${archetypeNumber(p.archetype)} ${archetype.name}`);
  name.title = archetype.text;
  const threat = segmentGauge('THREAT', 9, 7);
  threat.set(clampThreat(p.threat));
  card.body.append(
    name,
    fact('GROUND', ctx.describe(p.biome).name),
    fact('LIGHT', ctx.describe(p.timeOfDay).name),
    fact('WEATHER', ctx.describe(p.weather).name),
    threat.el,
  );
  if (p.modifiers.length > 0) {
    card.body.append(el('div', 'menu-dossier-key', 'CONDITIONS'));
    for (const id of p.modifiers) {
      const d = ctx.describe(id);
      const line = el('div', 'menu-brief-mod menu-prose', d.name);
      line.title = d.text;
      card.body.append(line);
    }
  }
  card.body.append(
    el('div', 'menu-dossier-key', 'RANGER OF THE DAY'),
    el('div', 'menu-daily-ranger menu-paper', status.rangerName),
    el('div', 'menu-dim menu-small menu-prose', 'STANDARD ISSUE. NO PODS TO PACK, NO PERKS, NOBODY TO LOSE BUT'
      + ' THE DAY.'),
  );
  return card.panel;
}

function playedBlock(entry: DailyEntry): HTMLElement {
  const stamped = stamp({ text: OUTCOME_WORD[entry.outcome], tone: cameHome(entry) ? 'green' : 'red' }, true);
  return box(
    'menu-daily-played',
    el('div', 'menu-dossier-key', "TODAY'S DROP HAS BEEN FLOWN"),
    stamped,
    fact('SCORE', Math.round(entry.score).toLocaleString('en-US')),
    fact('TIME', mmss(entry.secondsUsed)),
    el('div', 'menu-dim menu-small menu-prose', 'THE NEXT DROP OPENS AT 00:00 UTC.'),
  );
}

function table(ctx: Ctx, entries: readonly DailyEntry[], today: string): HTMLElement {
  const p = panel('LOCAL TABLE - NEWEST FIRST', 'menu-grow menu-daily-table');
  p.body.append(
    box(
      'menu-daily-row menu-daily-head',
      el('span', '', 'DATE'), el('span', '', 'MISSION'), el('span', '', 'RESULT'),
      el('span', 'menu-daily-num', 'SCORE'), el('span', 'menu-daily-num', 'TIME'),
    ),
  );
  for (const entry of tableOrder(entries)) {
    const mission = `${ctx.describe(entry.archetype).name} - ${ctx.describe(entry.biome).name}`;
    const row = box(
      entry.date === today ? 'menu-daily-row menu-daily-today' : 'menu-daily-row',
      el('span', 'menu-paper', entry.date),
      el('span', 'menu-daily-mission', mission),
      el('span', cameHome(entry) ? 'menu-good' : 'menu-bad', OUTCOME_WORD[entry.outcome]),
      el('span', 'menu-daily-num menu-paper', Math.round(entry.score).toLocaleString('en-US')),
      el('span', 'menu-daily-num', mmss(entry.secondsUsed)),
    );
    row.title = `${entry.rangerName} - ${mission}`;
    p.body.append(row);
  }
  if (entries.length === 0) {
    p.body.append(el('div', 'menu-empty menu-prose', 'NO DROPS FLOWN YET. TODAY IS A GOOD DAY.'));
  }
  return p.panel;
}

export function dailyDropScreen(ctx: Ctx, now: Date = new Date()): Screen {
  const today = utcDate(now);
  const f = frame('daily', 'DAILY DROP');
  f.info.append(el('span', 'menu-paper', today), el('span', '', 'UTC'));
  const back = button('BACK', () => ctx.nav.title());
  const error = el('div', 'menu-error menu-hidden');
  error.setAttribute('role', 'alert');
  const fail = (err: unknown): void => {
    error.textContent = errorMessage(err);
    show(error, true);
  };

  let status: DailyStatus | null = null;
  let entries: DailyEntry[] = [];
  try {
    status = ctx.api.daily(today);
    entries = ctx.api.records().daily;
  } catch (err) {
    fail(err);
  }

  const side = box('menu-col menu-daily-side');
  let focus: HTMLElement = back;
  if (status !== null) {
    const held = status;
    side.append(missionCard(ctx, held));
    if (held.played !== null) {
      side.append(playedBlock(held.played));
    } else {
      const warning = box('menu-daily-warning');
      warning.setAttribute('role', 'note');
      for (const line of ONE_ATTEMPT_LINES) warning.append(el('div', 'menu-prose', `! ${line}`));
      const fly = button('FLY', () =>
        confirmDailyFlight(ctx, true, () => {
          let deployment: Deployment;
          try {
            deployment = ctx.api.deployDaily(today);
          } catch (err) {
            fail(err);
            return;
          }
          ctx.fly(deployment);
        }),
      );
      const brief = button('BRIEFING', () =>
        ctx.nav.briefing({ mode: 'daily', params: held.params, node: null, rangerId: null, dailyDate: today }),
      );
      side.append(warning, error, box('menu-row menu-daily-actions', fly, brief, back));
      // One attempt: Enter on arrival reads the order; FLY is a deliberate second press.
      focus = brief;
    }
  }
  if (status === null || status.played !== null) side.append(error, box('menu-row menu-daily-actions', back));

  f.body.append(table(ctx, entries, today), side);
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => focus };
}
